import { useFormikContext } from 'formik'
import React, { useEffect, useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useParams } from 'react-router-dom'

import { useNavigate } from '@lib/react-router-dom'

import { useConfirmDeposit } from '@pages/Traders-Room/Deposit/Amount/hooks/useConfirmDeposit'
import { useEnsureCardholderName } from '@pages/Traders-Room/Deposit/Amount/hooks/useEnsureCardholderName'
import { hasTokenizedCardParams } from '@pages/Traders-Room/Deposit/CardDetails/helpers'
import { CryptoDepositInfo } from '@pages/Traders-Room/shared/components/CryptoDepositInfo/CryptoDepositInfo'
import { useFundingCurrencySelector } from '@pages/Traders-Room/shared/hooks/useFundingCurrencySelector'

import { useModal } from '@context/ModalsContext'

import { Caption1, Heading6 } from '@ui/Typography'
import { Button, IconButton } from '@ui/components/Button'
import { Input } from '@ui/components/Input/Input'
import { PopoverInput } from '@ui/components/Popover'
import { Tooltip } from '@ui/components/Tooltip'
import { InfoIcon } from '@ui/icons/Info.icon'
import { MinusIcon } from '@ui/icons/Minus.icon'
import { PlusIcon } from '@ui/icons/Plus.icon'

import { useApiClient } from '@utils/ApiClient'
import { ClientApiClient } from '@utils/clientApi'
import { useWindowResize } from '@utils/domUtils'
import { CommonPaths } from '@utils/path'

import {
  isPaymentProviderCryptoType,
  isPaymentProviderPaymentAgentType,
} from '@model/PaymentProviderType'

import { RawFormCheckBoxField } from '@global/formField/FormCheckBoxField'
import { createFormField } from '@global/formField/FormField'

import { useFormatNumber } from '@hooks/useFormatNumber'
import { useFundingAmountStepper } from '@hooks/useFundingAmountStepper'
import { useFundingNav } from '@hooks/useFundingNav'

import { Expandable } from '@shared/animations/Expandable/Expandable'
import { Box } from '@shared/components/Box'
import { CurrencyAmountChip } from '@shared/components/CurrencyAmountChip/CurrencyAmountChip'
import { FundingActionTextWrapper } from '@shared/components/Funding/FundingActionTextWrapper'
import { FundingCurrencySelect } from '@shared/components/Funding/FundingCurrencySelect'
import { FundingCurrencyTitle } from '@shared/components/Funding/FundingCurrencyTitle'
import { FundingFormWarningNote } from '@shared/components/Funding/FundingFormWarningNote'
import { FundingSummary } from '@shared/components/Funding/FundingSummary/FundingSummary'
import { FundingTermsConditions } from '@shared/components/Funding/FundingTermsConditions/FundingTermsConditions'
import { Notice } from '@shared/components/Notice'
import { FullScreenFooter } from '@shared/templates/FullScreenLayout'
import { FundingTemplate } from '@shared/templates/FundingTemplate'

import { useDepositTracker } from '@analytics/amplitude/deposit'

import {
  AgentInformation,
  AgentInformationType,
  useAgentInformationFetch,
} from '../../WalletAgentInformation'
import { DepositFormValues } from '../DepositFlowLayout/types'
import { DepositFormFields } from '../components/DepositFormFields'
import { getNormalizedWallet } from '../helpers'
import { DepositTarget } from '../hooks/types'
import { useGetCheckIfPhoneIsVerified } from '../hooks/useGetCheckIfPhoneIsVerified'
import { useGetDepositCurrencyLimits } from '../hooks/useGetDepositCurrencyLimits'
import { VerifyPhoneModal } from '../modals/VerifyPhone.modal'
import { DepositAmountField, MAX_ALLOWED_AMOUNT_SAFETY_CAP } from './DepositAmountField'
import {
  getDepositDestinationId,
  getDestinationCurrencyId,
  isTokenizedFlowEnabled,
} from './helpers'
import { useDepositBonusCampaigns } from './hooks/useDepositBonusCampaigns'
import { useDepositMarginBonus } from './hooks/useDepositMarginBonus'

const FormField = createFormField()

export const DepositAmountPage: React.FC<{
  target?: DepositTarget
  id?: string
  dashboardContext?: 'traders-room' | 'payment-agent'
}> = (props) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const tracker = useDepositTracker()
  useEnsureCardholderName()
  const contentRef = useRef<HTMLDivElement>(null)
  const apiClient = useApiClient(ClientApiClient)
  const { handleConfirm, submitError } = useConfirmDeposit()

  const { formatMoney } = useFormatNumber()

  const { target: targetProp } = props
  const { target: targetParam } = useParams<{
    target: DepositTarget
    id: string
    walletId?: string
  }>()

  const target = targetProp ?? targetParam

  const { values, setFieldValue, validateForm, isValid, isSubmitting } =
    useFormikContext<DepositFormValues>()
  const resolvedTarget: DepositTarget =
    target ?? (values?.wallet?.id ? 'wallet' : 'trading-account')

  const location = useLocation()
  const { go, state } = useFundingNav()
  const verifyPhoneModal = useModal(VerifyPhoneModal)
  const {
    isVerified: isPhoneVerified,
    isPhoneVerificationLoading,
    verificationEnabled,
  } = useGetCheckIfPhoneIsVerified()

  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search])
  const isThreeDSFailure = searchParams.get('3ds') === 'failure'
  const threeDSFailureReason = searchParams.get('reason')

  const provider = values.paymentProvider
  const providerForm = provider?.parameters?.form ?? []
  const tokenizedEnabled = hasTokenizedCardParams(providerForm)
  const providerCurrencyId = provider?.currency?.id || ''
  const providerFee = Number(provider?.currency?.fee || 0)

  const destinationCurrencyId = getDestinationCurrencyId({ target: resolvedTarget, values })
  const summaryCurrency = providerCurrencyId || destinationCurrencyId || 'USD'

  const tokenized = isTokenizedFlowEnabled(provider?.parameters?.form || [])

  const normalizedWallet = useMemo(() => getNormalizedWallet(values), [values])

  const { conversionRate, depositCurrencyLimits } = useGetDepositCurrencyLimits({
    wallet: normalizedWallet,
    paymentProvider: provider,
    conversionRate: values.conversionRate,
  })

  const { amountValue, canDecrease, canIncrease, onDecrease, onIncrease } = useFundingAmountStepper(
    {
      amount: values.amount,
      fixedIncrementStep: provider?.parameters?.incrementStep,
      fixedDecrementStep: provider?.parameters?.decrementStep,
      maxValue: depositCurrencyLimits?.maxAmount || MAX_ALLOWED_AMOUNT_SAFETY_CAP,
      setAmount: (n) => setFieldValue('amount', n),
    }
  )
  const { marginBonus } = useDepositMarginBonus(
    values?.tradingAccount?.id,
    values?.tradingAccount?.campaign?.id,
    values.amount
  )

  useEffect(() => {
    if (conversionRate !== values.conversionRate) {
      setFieldValue('conversionRate', conversionRate)
    }
  }, [conversionRate, setFieldValue, values.conversionRate])

  const totalValue = amountValue + providerFee

  const currencySelector = useFundingCurrencySelector({
    paymentProvider: provider,
    fields: values.fields,
    setFieldValue,
    hideSinglePaymentCurrency: true,
  })
  const isFundingCurrencySelected =
    !currencySelector.isVisible || Boolean(currencySelector.selectedCurrencyId)

  const isCryptoProvider = isPaymentProviderCryptoType(provider?.providerCategory?.id)
  const isPaymentAgentProvider = isPaymentProviderPaymentAgentType(provider?.providerCategory?.id)

  const walletId = useMemo(
    () => getDepositDestinationId({ target: resolvedTarget, values }),
    [resolvedTarget, values]
  )

  const {
    paymentAgent,
    isLoading: isAgentLoading,
    forceRefresh: forceRefreshAgent,
  } = useAgentInformationFetch(walletId, isPaymentAgentProvider ? provider.id! : '')

  useEffect(() => {
    if (isPaymentAgentProvider && paymentAgent) {
      setFieldValue('paymentAgent', paymentAgent)
    }
  }, [isPaymentAgentProvider, paymentAgent, setFieldValue])

  const {
    availableBonusCampaigns,
    selectedBonusCampaign,
    isBonusLoading,
    isBonusCampaignValid,
    isBonusDepositSectionVisible,
    info,
  } = useDepositBonusCampaigns({
    target: resolvedTarget,
  })

  const shouldVerifyPhone = verificationEnabled && !isPhoneVerified

  useEffect(() => {
    validateForm()
  }, [validateForm])

  useEffect(() => {
    const state = tracker.getState()
    if (!state.started || state.ended || state.submitted || state.currentStep === 'amount_terms') {
      return
    }

    tracker.stepChanged({
      from: state.currentStep,
      to: 'amount_terms',
      direction: 'forward',
    })
  }, [tracker])

  const onContinue = async () => {
    if (!isFundingCurrencySelected) return

    const errors = await validateForm()
    if (Object.keys(errors).length > 0) return
    if (!isBonusCampaignValid) return

    if (isPaymentAgentProvider && paymentAgent) {
      if (provider.id && !paymentAgent.powerOfAttorney.accepted) {
        await apiClient.paymentAgentAcceptTerms(provider.id)
        forceRefreshAgent()
      } else if (provider.id && paymentAgent.powerOfAttorney.accepted) {
        await navigate(CommonPaths.PaymentAgentWallets)
      }
      return
    }

    tracker.stepChanged({ from: 'destination_method', to: 'amount_terms', direction: 'forward' })

    if (tokenized) go('../card-details')
    else return handleConfirm()
  }

  const getSubmitButtonLabel = (): string => {
    if (isPaymentAgentProvider && paymentAgent && !paymentAgent.powerOfAttorney.accepted) {
      return t('Accept Agent')
    }

    if (isPaymentAgentProvider && paymentAgent?.powerOfAttorney.accepted) {
      return t('Trading Account.Exit')
    }

    if (tokenized) return t('Continue')

    return t('Confirm')
  }

  const showFooterNote = !isCryptoProvider && !isPaymentAgentProvider

  const isMobile = useWindowResize()

  return (
    <FundingTemplate
      contentRef={contentRef}
      isLoading={isPhoneVerificationLoading || isAgentLoading}
      onBack={() => {
        tracker.stepChanged({
          from: 'amount_terms',
          to: 'destination_method',
          direction: 'backward',
        })
        go(`/dashboard/${values.dashboardContext || 'traders-room'}/deposit/${resolvedTarget}`, {
          replace: true,
          state: {
            selectedWalletId: values.wallet?.id,
            tradingAccountId: values.tradingAccount?.id,
          },
        })
      }}
      maxWidth={456}
    >
      <Box flex direction='column' gap='x32' fullWidth align='center' style={{ minHeight: '100%' }}>
        {submitError && (
          <Notice id='deposit-preview-submit-error' type='danger' message={submitError} />
        )}

        {isThreeDSFailure && (
          <Notice
            id='three-ds-failure'
            type='danger'
            message={threeDSFailureReason || t('Something went wrong! Please try again later')}
          />
        )}

        {currencySelector.isVisible ? (
          <Heading6 medium>{t('Deposit')}</Heading6>
        ) : (
          <FundingCurrencyTitle
            value={currencySelector.selectedCurrencyId || summaryCurrency}
            label={t('Deposit')}
          />
        )}
        {isPaymentAgentProvider && values.tradingAccount && (
          <Notice
            id='campaign-deposit-agent-info'
            type='warning'
            title={t('Wallet.Important!').toUpperCase()}
            message={t(
              'Wallet.Please make sure to let the agent know that you want to make a deposit to the trading account',
              { account: values.tradingAccount.name }
            )}
          />
        )}
        {!isPaymentAgentProvider && (
          <Box flex align='start' gap='x16' fullWidth>
            <Box pt='x8'>
              <IconButton.Ghost
                icon={{ icon: MinusIcon, color: 'text-low-em' }}
                size='md'
                disabled={!canDecrease}
                onClick={onDecrease}
              />
            </Box>
            <DepositAmountField
              target={resolvedTarget}
              onBlur={() => {
                if (values.amount) {
                  tracker.amountEntered({
                    amount: values.amount,
                    currency: providerCurrencyId,
                    amount_source: 'manual',
                  })
                }
              }}
            />
            <Box pt='x8'>
              <IconButton.Ghost
                icon={{ icon: PlusIcon, color: 'text-low-em' }}
                size='md'
                disabled={!canIncrease}
                onClick={onIncrease}
              />
            </Box>
          </Box>
        )}

        {!isPaymentAgentProvider && (
          <CurrencyAmountChip
            minValue={depositCurrencyLimits?.minAmount || provider?.currency?.minAmount}
            onSelect={(value) => {
              tracker.amountEntered({
                amount: value,
                currency: providerCurrencyId,
                amount_source: 'preset',
              })
            }}
          />
        )}

        {currencySelector.isVisible && (
          <FundingCurrencySelect
            options={currencySelector.options}
            value={currencySelector.selectedCurrencyId}
            fieldName={currencySelector.fieldName}
            label={currencySelector.label}
            required={currencySelector.required}
          />
        )}

        {marginBonus?.isMarginBonus && (
          <Box flex direction='column' gap='x12' alignSelf='stretch'>
            <Box
              flex
              align='flex-start'
              gap='x12'
              alignSelf='stretch'
              direction={isMobile ? 'column' : 'row'}
            >
              <Input
                value={`${marginBonus.marginBonusPercentage.toFixed(2)}% ${t('Bonus')}`}
                disabled
                readOnly
                fullWidth
              />
              <Input
                value={formatMoney(marginBonus.marginBonusAmount, summaryCurrency)}
                renderRightIconJSX={
                  marginBonus.maxBonusReached ? (
                    <Tooltip
                      content={t(
                        'The maximum amount of bonus you can get according to the Campaign T&C is',
                        {
                          amount: formatMoney(marginBonus.maxBonusAmount, summaryCurrency),
                        }
                      )}
                    >
                      <InfoIcon color='text-low-em' />
                    </Tooltip>
                  ) : null
                }
                readOnly
                fullWidth
              />
            </Box>
          </Box>
        )}

        {!tokenizedEnabled && <DepositFormFields />}

        {isPaymentAgentProvider && paymentAgent && (
          <Box fullWidth>
            <AgentInformation type={AgentInformationType.Deposit} paymentAgent={paymentAgent} />
          </Box>
        )}

        <FundingSummary
          paymentProvider={values.paymentProvider}
          wallet={values.wallet}
          tradingAccount={values.tradingAccount}
          walletPaymentProviderLogo={state?.walletPaymentProvider?.logo}
        />

        {isBonusDepositSectionVisible && (
          <Box flex direction='column' gap='x16' fullWidth>
            <RawFormCheckBoxField
              name='isBonusSelected'
              size='md'
              onChange={
                shouldVerifyPhone
                  ? () => verifyPhoneModal.open()
                  : (e) => setFieldValue('isBonusSelected', e)
              }
              checked={values.isBonusSelected}
            >
              {t('Wallet.Get a bonus on deposit')}
            </RawFormCheckBoxField>

            <Expandable fullWidth isExpanded={values.isBonusSelected}>
              <Box flex direction='column' gap='x16' fullWidth pt='x16'>
                {availableBonusCampaigns && availableBonusCampaigns.length > 1 && (
                  <PopoverInput
                    name='bonusCampaignId'
                    label={t('Wallet.Bonus Type')}
                    value={values.bonusCampaignId}
                    options={availableBonusCampaigns.map(({ bonusCampaign }) => ({
                      id: bonusCampaign.id,
                      name: bonusCampaign.name,
                    }))}
                    onChange={(option) => setFieldValue('bonusCampaignId', option.id)}
                  />
                )}
                <FormField
                  name='bonusAmount'
                  label={t('Wallet.Bonus size')}
                  placeholder={t('Wallet.Bonus size')}
                  value={`${selectedBonusCampaign?.bonusAmount.toFixed(2)} ${summaryCurrency}`}
                  disabled
                  loading={isBonusLoading}
                  renderRightIconJSX={info}
                />
              </Box>
            </Expandable>
          </Box>
        )}

        <FundingTermsConditions
          paymentAgent={isPaymentAgentProvider ? paymentAgent : undefined}
          selectedBonusCampaign={selectedBonusCampaign}
        />

        <FullScreenFooter contentRef={contentRef}>
          {showFooterNote && (
            <FundingActionTextWrapper key={`${totalValue}-${summaryCurrency}`}>
              <Caption1 color='text-low-em'>
                {t('Subscriptions.Total')} {totalValue.toFixed(2)} {summaryCurrency} {'\u2022'}{' '}
                {t('Fee')} {providerFee.toFixed(2)} {summaryCurrency}
              </Caption1>
            </FundingActionTextWrapper>
          )}
          <Button.Primary
            size='lg'
            type='button'
            fullWidth
            disabled={!isValid || !isBonusCampaignValid || !isFundingCurrencySelected}
            loading={isSubmitting || isBonusLoading || isAgentLoading || isSubmitting}
            onClick={onContinue}
          >
            {getSubmitButtonLabel()}
          </Button.Primary>
        </FullScreenFooter>

        {isCryptoProvider && provider?.id && <CryptoDepositInfo paymentProviderId={provider.id} />}
        <FundingFormWarningNote paymentProvider={provider} />
      </Box>
    </FundingTemplate>
  )
}
